'use client'

// React
import { useState, useCallback } from 'react'

// Next.js
import dynamic from 'next/dynamic'

// Animation
import { motion } from 'framer-motion'

// design-studio
import {
  Container,
  fadeInUp,
  Section,
  SectionHeader,
  spaceGrotesk,
  Tab,
  TabGroup,
  TabList,
} from 'design-studio'

// Charts — styles & data (used by the synchronous shell)
import {
  chartStyles,
  metricColorMap,
  modelColors,
} from './charts/common/styles'
import {
  boxPlotData,
  metricAveragesData,
  metricMediansData,
  modelAveragesData,
  modelComparisonData,
} from './charts/common/data'

// Recharts chart panels — lazy so Recharts JS is not in the initial bundle
import type {
  BarPanelUpdate,
  GroupedPanelUpdate,
  RadarPanelUpdate,
} from './performance-chart-panels'

const PerformanceChartPanels = dynamic(
  () =>
    import('./performance-chart-panels').then((m) => ({
      default: m.PerformanceChartPanels,
    })),
  {
    ssr: false,
    loading: () => (
      <div
        className={`${chartStyles.tabPanel.base} flex min-h-[340px] animate-pulse items-center justify-center bg-slate-800/40`}
      >
        <span className="text-xs text-slate-500">Loading charts…</span>
      </div>
    ),
  },
)

// Utils
import { Join } from 'design-studio/lib'

// ── Panel types ──────────────────────────────────────────────────────────────

type PanelEntry = { name: string; value: number; color: string }
type PanelData = {
  heading: string
  entries: PanelEntry[]
  /** Whether to show the "LLM + CodeLogician = 1.00" reference row */
  showCLRef?: boolean
  /** Unit label appended to values, e.g. "IQR" */
  unit?: 'percent' | 'iqr'
}

function shortName(key: string): string {
  return key
    .replace('anthropic/ ', '')
    .replace('google/ ', '')
    .replace('openai/ ', '')
    .replace('x-ai/ ', '')
}

const colorLookup = modelColors as Record<string, string>

// Tab 0 & 2 — model rankings
const modelPanelEntries: PanelEntry[] = modelAveragesData.map((m) => ({
  name: shortName(m.displayName),
  value: m.averageScore,
  color: colorLookup[m.displayName] ?? '#94a3b8',
}))

// Tab 1 — best score per metric (which model leads each category)
const MODEL_KEYS = [
  'anthropic/ claude-opus-4.6',
  'anthropic/ claude-opus-4.5',
  'anthropic/ claude-sonnet-4.5',
  'google/gemini-3-pro',
  'openai/ gpt-5.2',
  'x-ai/ grok-code-fast-1',
] as const

const metricLeaderEntries: PanelEntry[] = modelComparisonData
  .map((d) => {
    let bestModel: string = MODEL_KEYS[0]
    let bestScore = 0
    for (const m of MODEL_KEYS) {
      const score = d[m as keyof typeof d] as number
      if (score > bestScore) {
        bestScore = score
        bestModel = m
      }
    }
    return {
      name: d.metric,
      value: bestScore,
      color: colorLookup[bestModel] ?? '#94a3b8',
    }
  })
  .sort((a, b) => b.value - a.value)

// Tab 3 — metric averages
const metricAvgPanelEntries: PanelEntry[] = [...metricAveragesData]
  .sort((a, b) => b.score - a.score)
  .map((m) => ({
    name: m.metric,
    value: m.score,
    color: metricColorMap[m.metric] ?? '#3b82f6',
  }))

// Tab 4 — metric medians
const metricMedianPanelEntries: PanelEntry[] = [...metricMediansData]
  .sort((a, b) => b.medianScore - a.medianScore)
  .map((m) => ({
    name: m.metric,
    value: m.medianScore,
    color: metricColorMap[m.metric] ?? '#3b82f6',
  }))

// Tab 5 — IQR (q3 − q1) per metric sorted by spread descending
const metricVariabilityEntries: PanelEntry[] = [...boxPlotData]
  .map((d) => ({
    name: d.metric,
    value: d.q3 - d.q1,
    color: metricColorMap[d.metric] ?? '#3b82f6',
  }))
  .sort((a, b) => b.value - a.value)

const DEFAULT_PANELS: PanelData[] = [
  { heading: 'Model rankings', entries: modelPanelEntries, showCLRef: true }, // 0 Radar
  {
    heading: 'Top score per metric',
    entries: metricLeaderEntries,
    showCLRef: true,
  }, // 1 Grouped
  { heading: 'Model rankings', entries: modelPanelEntries, showCLRef: true }, // 2 Model Averages
  {
    heading: 'Metric averages',
    entries: metricAvgPanelEntries,
    showCLRef: true,
  }, // 3 Metric Averages
  {
    heading: 'Metric medians',
    entries: metricMedianPanelEntries,
    showCLRef: true,
  }, // 4 Median Scores
  {
    heading: 'Score variability',
    entries: metricVariabilityEntries,
    showCLRef: false,
    unit: 'iqr',
  }, // 5 Distribution
]

// ── SidePanel ────────────────────────────────────────────────────────────────

const CL_COLOR = modelColors['LLM + CodeLogician']

function SidePanel({ panel }: { panel: PanelData }) {
  const isIqr = panel.unit === 'iqr'
  // For IQR panel the max value sets the bar scale (not always 1.0)
  const maxValue = isIqr ? Math.max(...panel.entries.map((e) => e.value), 1) : 1

  function formatValue(v: number) {
    if (isIqr) return `±${(v * 100).toFixed(0)}%`
    return `${(v * 100).toFixed(1)}%`
  }

  return (
    <div className="mt-3 flex flex-col gap-1.5 rounded-xl border border-slate-200/30 bg-slate-50/60 p-3 dark:border-slate-700/50 dark:bg-slate-800/40">
      <p className="mb-1 truncate text-[10px] font-semibold tracking-widest text-slate-400 uppercase dark:text-slate-500">
        {panel.heading}
      </p>

      {(panel.showCLRef ?? true) && (
        <>
          <div className="flex items-center gap-2">
            <div
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: CL_COLOR }}
            />
            <span className="flex-1 truncate text-[11px] text-slate-600 dark:text-slate-300">
              LLM + CodeLogician
            </span>
            <span
              className="text-[11px] font-bold tabular-nums"
              style={{ color: CL_COLOR }}
            >
              100%
            </span>
          </div>
          <div className="-mx-1 my-0.5 h-px bg-slate-200/60 dark:bg-slate-700/60" />
        </>
      )}

      {panel.entries.map((e) => (
        <div key={e.name} className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <div
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: e.color }}
            />
            <span className="flex-1 truncate text-[11px] text-slate-600 dark:text-slate-300">
              {shortName(e.name)}
            </span>
            <span className="text-[11px] font-semibold text-slate-700 tabular-nums dark:text-slate-200">
              {formatValue(e.value)}
            </span>
          </div>
          <div className="ml-3.5 h-[3px] overflow-hidden rounded-full bg-slate-200/50 dark:bg-slate-700/50">
            <div
              className="h-full transition-all duration-200"
              style={{
                width: `${(e.value / maxValue) * 100}%`,
                backgroundColor: e.color,
                opacity: 0.85,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Section ──────────────────────────────────────────────────────────────────

const TAB_LABELS = [
  'Model Comparison',
  'Grouped Comparison',
  'Model Averages',
  'Metric Averages',
  'Median Scores',
  'Score Distribution',
]

export function Performance() {
  const [tabIndex, setTabIndex] = useState(0)
  const [radarPanel, setRadarPanel] = useState<RadarPanelUpdate>(null)
  const [groupedPanel, setGroupedPanel] = useState<GroupedPanelUpdate>(null)
  const [barPanel, setBarPanel] = useState<BarPanelUpdate>(null)

  const activeHoverPanel: {
    heading: string
    entries: Array<{ name: string; value: number; color: string }>
  } | null =
    tabIndex === 0 ? radarPanel : tabIndex === 1 ? groupedPanel : barPanel

  const currentPanel: PanelData = activeHoverPanel
    ? {
        heading: activeHoverPanel.heading,
        entries: activeHoverPanel.entries,
        // Radar (tab 0): CL is filtered out of entries — keep the ref row at top.
        // All other charts: CL is already included as an entry — don't duplicate it.
        showCLRef: tabIndex === 0,
      }
    : DEFAULT_PANELS[tabIndex]!

  const handleRadarPanelUpdate = useCallback((state: RadarPanelUpdate) => {
    setRadarPanel(state)
  }, [])

  const handleGroupedPanelUpdate = useCallback((state: GroupedPanelUpdate) => {
    setGroupedPanel(state)
  }, [])

  const handleBarPanelUpdate = useCallback((state: BarPanelUpdate) => {
    setBarPanel(state)
  }, [])

  return (
    <Section
      id="performance"
      className="relative z-10 z-20 py-16 sm:py-24 lg:py-32"
    >
      <Container className="max-w-8xl">
        <SectionHeader
          pretitle="Benchmarks"
          title={
            <>
              CodeLogician <br /> Performance Metrics
            </>
          }
          description="Comprehensive evaluation of LLM performance across multiple models and metrics"
          className="mb-12 items-end"
        />

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-10"
        >
          <TabGroup className="grid gap-2 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80 p-2 backdrop-blur-md lg:grid-cols-[1fr_4fr] dark:border-slate-700/30 dark:bg-cyan-950/50">
            {/* Left column: tab list only */}
            <div className="flex flex-col lg:border-r lg:border-slate-200 lg:dark:border-slate-700/30">
              <TabList className="flex flex-col justify-start gap-1 p-2">
                {TAB_LABELS.map((label, index) => (
                  <Tab
                    key={label}
                    onClick={() => setTabIndex(index)}
                    className={({ selected }) =>
                      Join(
                        'group cursor-pointer rounded-full py-1.5 pl-3 text-left transition-all duration-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-teal-500/70',
                        selected
                          ? 'bg-white shadow-sm dark:bg-teal-500/40'
                          : 'border-transparent hover:bg-teal-50/20 dark:hover:bg-teal-600/50',
                      )
                    }
                  >
                    <span
                      className={Join(
                        spaceGrotesk.className,
                        chartStyles.typography.label,
                      )}
                    >
                      {label}
                    </span>
                  </Tab>
                ))}
              </TabList>
            </div>

            {/* Right column: chart (60%) + info panel (30%), stacked on mobile */}
            <div className="flex flex-col gap-3 p-2 lg:flex-row">
              <div className="min-w-0 lg:flex-2">
                <PerformanceChartPanels
                  onRadarPanelUpdate={handleRadarPanelUpdate}
                  onGroupedPanelUpdate={handleGroupedPanelUpdate}
                  onBarPanelUpdate={handleBarPanelUpdate}
                />
              </div>

              {/* Info panel: beside diagram on desktop, below on mobile */}
              <div className="lg:flex-1 lg:self-start">
                <SidePanel panel={currentPanel} />
              </div>
            </div>
          </TabGroup>
        </motion.div>

        <motion.p
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, margin: '-50px 0px' }}
          className="mx-auto max-w-6xl text-base text-slate-300"
        >
          Benchmark data and analysis in the{' '}
          <a
            href="https://github.com/imandra-ai/code-logic-bench"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 underline-offset-2 hover:text-blue-300 hover:underline"
          >
            code-logic-bench repository
          </a>
          .
        </motion.p>
      </Container>
    </Section>
  )
}
